import {
    curatedSearchConfig,
    rangeFiltersState,
} from "@/services/algoliaRouting.blade";

function countCuratedModelSelections(): number {
    const curatedModels = curatedSearchConfig.value.filters?.refinementList?.model;
    if (Array.isArray(curatedModels) && curatedModels.length > 0) {
        return curatedModels.length;
    }

    const landingModels =
        window.__MARKETPLACE_CONFIG__?.landingData
            ?.concentratedModelDesignations ?? [];
    if (landingModels.length > 0) {
        return landingModels.length;
    }

    return (
        window.__MARKETPLACE_CONFIG__?.initialModelsFacet?.filter(
            (item) => item.isRefined,
        ).length ?? 0
    );
}

function countCuratedMakeSelections(): number {
    const curatedMake = curatedSearchConfig.value.filters?.menu?.make;
    if (typeof curatedMake === "string" && curatedMake !== "") {
        return 1;
    }

    return (
        window.__MARKETPLACE_CONFIG__?.initialMakesFacet?.filter(
            (item) => item.isRefined,
        ).length ?? 0
    );
}

export function countCheckedInContainer(
    container: ParentNode | null,
    checkboxSelector: string,
): number {
    if (!container) return 0;

    return container.querySelectorAll<HTMLInputElement>(
        `${checkboxSelector}:checked`,
    ).length;
}

export function countMakeFacetActive(root: ParentNode = document): number {
    const container = root.querySelector("#action-bar-make");
    const checked = countCheckedInContainer(container, ".RefinementList__checkbox");
    if (checked > 0) {
        return checked;
    }

    return countCuratedMakeSelections();
}

export function countEnergyFacetActive(root: ParentNode = document): number {
    const container = root.querySelector("#action-bar-energy");
    return countCheckedInContainer(container, ".facette-button__input");
}

export function countModelFacetActive(root: ParentNode = document): number {
    const container = root.querySelector("#action-bar-model");
    const checked = countCheckedInContainer(container, ".RefinementList__checkbox");
    if (checked > 0) {
        return checked;
    }

    return countCuratedModelSelections();
}

export function countMileageFacetActive(
    rangeState = rangeFiltersState.value,
    curated = curatedSearchConfig.value,
): number {
    const range = rangeState.mileage;
    if (!range || (range.min === undefined && range.max === undefined)) {
        return 0;
    }

    if (curated.isActive && curated.filters) {
        const curatedMin = curated.filters.mileage_min as number | undefined;
        const curatedMax = curated.filters.mileage_max as number | undefined;
        if (range.min === curatedMin && range.max === curatedMax) {
            return 0;
        }
    }

    return 1;
}

export function countPriceFacetActive(
    rangeState = rangeFiltersState.value,
    curated = curatedSearchConfig.value,
): number {
    const range = rangeState.price;
    if (!range || (range.min === undefined && range.max === undefined)) {
        return 0;
    }

    if (curated.isActive && curated.filters) {
        const curatedMin = curated.filters.price_min as number | undefined;
        const curatedMax = curated.filters.price_max as number | undefined;
        if (range.min === curatedMin && range.max === curatedMax) {
            return 0;
        }
    }

    return 1;
}

export function resolveActionBarFacetActiveCount(mountId: string): number {
    switch (mountId) {
        case "action-bar-make":
            return countMakeFacetActive();
        case "action-bar-model":
            return countModelFacetActive();
        case "action-bar-energy":
            return countEnergyFacetActive();
        case "action-bar-mileage":
            return countMileageFacetActive();
        case "action-bar-price":
            return countPriceFacetActive();
        default:
            return 0;
    }
}

export interface FacetActiveValues {
    values: string[];
    /** Valeurs ajoutées par le visiteur (pas celles imposées par la page), que la croix retire. */
    removableValues: string[];
}

/** État de recherche en direct (`SearchParameters` d'InstantSearch), limité à ce qu'on lit. */
export interface FacetRefinementState {
    facetsRefinements?: Record<string, string[]>;
    disjunctiveFacetsRefinements?: Record<string, string[]>;
    hierarchicalFacetsRefinements?: Record<string, string[]>;
}

/** Facettes à valeurs de la barre (les plages prix / kilométrage n'ont pas de libellé simple). */
export const ACTION_BAR_VALUE_FACET_ATTRIBUTES: Readonly<Record<string, string>> = {
    "action-bar-make": "make",
    "action-bar-model": "model",
    "action-bar-energy": "energy_type",
};

const CHECKED_INPUT_SELECTORS: Readonly<Record<string, string>> = {
    "action-bar-make": ".RefinementList__checkbox:checked",
    "action-bar-model": ".RefinementList__checkbox:checked",
    "action-bar-energy": ".facette-button__input:checked",
};

function uniqueValues(values: unknown[]): string[] {
    return [
        ...new Set(
            values
                .filter((value): value is string => typeof value === "string")
                .map((value) => value.trim())
                .filter((value) => value !== ""),
        ),
    ];
}

/** Valeurs imposées par la landing ou la recherche curée : affichées, jamais retirables. */
function curatedBaselineValues(attribute: string): string[] {
    const config = curatedSearchConfig.value;
    const filters = config.isActive ? config.filters : undefined;
    const landingModels =
        attribute === "model" && typeof window !== "undefined"
            ? (window.__MARKETPLACE_CONFIG__?.landingData?.concentratedModelDesignations ?? [])
            : [];

    return uniqueValues([
        filters?.menu?.[attribute],
        ...(filters?.refinementList?.[attribute] ?? []),
        ...landingModels,
    ]);
}

function liveRefinementValues(state: FacetRefinementState, attribute: string): string[] {
    return uniqueValues([
        ...(state.hierarchicalFacetsRefinements?.[attribute] ?? []),
        ...(state.disjunctiveFacetsRefinements?.[attribute] ?? []),
        ...(state.facetsRefinements?.[attribute] ?? []),
    ]);
}

/** Avant le premier rendu d'InstantSearch : cases cochées, sinon sélection servie avec la page. */
function hydrationFallbackValues(mountId: string, root: ParentNode): string[] {
    const container = root.querySelector(`#${mountId}`);
    const pinned = container
        ?.querySelector("[data-make-pinned]:not([hidden]) .RefinementList__labelText")
        ?.textContent;
    const checked = container
        ? Array.from(container.querySelectorAll(CHECKED_INPUT_SELECTORS[mountId])).map((input) => {
              const label = input.closest("label");
              return (
                  label?.querySelector(".RefinementList__labelText")?.textContent ??
                  label?.querySelector("span")?.textContent
              );
          })
        : [];
    const fromDom = uniqueValues([pinned, ...checked]);
    if (fromDom.length > 0) return fromDom;

    const initialFacet =
        typeof window === "undefined"
            ? undefined
            : mountId === "action-bar-make"
              ? window.__MARKETPLACE_CONFIG__?.initialMakesFacet
              : mountId === "action-bar-model"
                ? window.__MARKETPLACE_CONFIG__?.initialModelsFacet
                : undefined;

    return uniqueValues((initialFacet ?? []).filter((item) => item.isRefined).map((item) => item.label));
}

/**
 * Valeurs actives d'une facette de la barre (« Aston Martin », « DBX »…), pour les écrire dans sa
 * pastille. Avec l'état de recherche en direct, il fait foi (les cases des panneaux peuvent rester
 * cochées d'après la sélection initiale) ; sans lui, on lit la page.
 */
export function resolveActionBarFacetActiveValues(
    mountId: string,
    state?: FacetRefinementState | null,
    root?: ParentNode,
): FacetActiveValues {
    const attribute = ACTION_BAR_VALUE_FACET_ATTRIBUTES[mountId];
    if (!attribute) {
        return { values: [], removableValues: [] };
    }

    const baseline = curatedBaselineValues(attribute);

    if (state) {
        const removableValues = liveRefinementValues(state, attribute).filter(
            (value) => !baseline.includes(value),
        );
        return { values: uniqueValues([...baseline, ...removableValues]), removableValues };
    }

    const values = uniqueValues([
        ...baseline,
        ...(root ?? (typeof document !== "undefined" ? document : null)
            ? hydrationFallbackValues(mountId, root ?? document)
            : []),
    ]);

    return { values, removableValues: [] };
}
